import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DatabaseService } from "../services/database";
import { API_ENDPOINTS } from "../config/api";
import { UICollege, UIEssay, SharedEssay } from "../types/database";

export interface DashboardData {
  user: any;
  colleges: UICollege[];
  essays: UIEssay[];
  sharedEssays: SharedEssay[];
  subscriptionData: {
    plan: string;
    usageCount: number;
    canAnalyze: boolean;
  };
  userRequests: any[];
}

export interface ReviewerDashboardData {
  user: any;
  pendingRequests: any[];
  activeRequests: any[];
  completedRequests: any[];
  subscriptionData: {
    plan: string;
    usageCount: number;
    canAnalyze: boolean;
  };
}

export interface PlanningData {
  colleges: any[];
  milestones: any[];
}

// Query keys for consistent caching
export const queryKeys = {
  dashboard: (userId: string) => ["dashboard", userId],
  reviewerDashboard: (userId: string) => ["reviewerDashboard", userId],
  userProfile: (userId: string) => ["user", userId],
  userColleges: (userId: string) => ["colleges", userId],
  userEssays: (userId: string) => ["essays", userId],
  userSharedEssays: (userId: string) => ["sharedEssays", userId],
  userRequests: (userId: string) => ["requests", userId],
  subscription: (userId: string) => ["subscription", userId],
  pendingRequests: (userId: string) => ["pendingRequests", userId],
  activeRequests: (userId: string) => ["activeRequests", userId],
  completedRequests: (userId: string) => ["completedRequests", userId],
  planningData: (userId: string) => ["planningData", userId],
};

// Fetch unified dashboard data
const fetchDashboardData = async (userId: string): Promise<DashboardData> => {
  try {
    // Try the unified endpoint first (when backend implements it)
    const response = await fetch(API_ENDPOINTS.getUserDashboard(userId));
    if (response.ok) {
      return await response.json();
    }
  } catch (error) {
    console.log(
      "Unified dashboard endpoint not available, falling back to individual calls"
    );
  }

  // Fallback to individual API calls
  const [
    dbUser,
    collegesData,
    essaysData,
    sharedEssaysData,
    subscriptionResult,
    userRequests,
  ] = await Promise.all([
    DatabaseService.ensureUserExists({ id: userId, email: "temp@temp.com" }),
    DatabaseService.getUserColleges(userId),
    DatabaseService.getUserEssays(userId),
    DatabaseService.getUserSharedEssays(userId),
    DatabaseService.checkUserCanAnalyze(userId),
    fetch(API_ENDPOINTS.getStudentRequests(userId))
      .then((res) => (res.ok ? res.json() : []))
      .catch(() => []),
  ]);

  return {
    user: dbUser,
    colleges: collegesData,
    essays: essaysData,
    sharedEssays: sharedEssaysData,
    subscriptionData: {
      plan: subscriptionResult.subscriptionPlan,
      usageCount: subscriptionResult.usageCount,
      canAnalyze: subscriptionResult.canAnalyze,
    },
    userRequests,
  };
};

// Fetch unified reviewer dashboard data
const fetchReviewerDashboardData = async (
  userId: string
): Promise<ReviewerDashboardData> => {
  try {
    // Try the unified endpoint first (when backend implements it)
    const response = await fetch(
      API_ENDPOINTS.getReviewerDashboard?.(userId) || ""
    );
    if (response.ok) {
      return await response.json();
    }
  } catch (error) {
    console.log(
      "Unified reviewer dashboard endpoint not available, falling back to individual calls"
    );
  }

  // Fallback to individual API calls
  const [dbUser, pendingData, activeData, completedData, subscriptionResult] =
    await Promise.all([
      DatabaseService.ensureUserExists({ id: userId, email: "temp@temp.com" }),
      fetch(API_ENDPOINTS.getPendingRequests(userId)).then((res) => res.json()),
      fetch(API_ENDPOINTS.getActiveRequests(userId)).then((res) => res.json()),
      fetch(API_ENDPOINTS.getCompletedRequests(userId)).then((res) =>
        res.json()
      ),
      DatabaseService.checkUserCanAnalyze(userId),
    ]);

  // Transform the data to match our interface
  const transformRequest = (
    req: any,
    status: "pending" | "accepted" | "completed" | "declined"
  ) => ({
    id: req.id,
    studentName: req.student_name || "Unknown Student",
    studentEmail: req.student_email || "",
    essayTitle: req.title || "Essay Review",
    title: req.title,
    description: req.description,
    essayType: req.essay_type,
    priority: req.priority,
    deadline: req.deadline,
    submittedDate: req.created_at,
    status: status,
    quotedPrice: req.quoted_price,
    paymentStatus: req.payment_status,
    quotedAt: req.quoted_at,
    paidAt: req.paid_at,
    essays: req.essays || [],
  });

  return {
    user: dbUser,
    pendingRequests: pendingData.map((req: any) =>
      transformRequest(req, "pending")
    ),
    activeRequests: activeData.map((req: any) =>
      transformRequest(req, "accepted")
    ),
    completedRequests: completedData.map((req: any) =>
      transformRequest(req, "completed")
    ),
    subscriptionData: {
      plan: subscriptionResult.subscriptionPlan,
      usageCount: subscriptionResult.usageCount,
      canAnalyze: subscriptionResult.canAnalyze,
    },
  };
};

// Fetch planning data
const fetchPlanningData = async (userId: string): Promise<PlanningData> => {
  const [collegesData, milestonesData] = await Promise.all([
    DatabaseService.getPlanningColleges(userId),
    DatabaseService.getPlanningMilestones(userId),
  ]);

  return {
    colleges: collegesData || [],
    milestones: milestonesData || [],
  };
};

// Fetch planning events (calendar)
const fetchPlanningEvents = async (userId: string) => {
  return DatabaseService.getPlanningEvents(userId);
};

// Hook for dashboard data with caching
export const useDashboardData = (userId: string | null) => {
  return useQuery({
    queryKey: queryKeys.dashboard(userId || ""),
    queryFn: () => fetchDashboardData(userId!),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (renamed from cacheTime)
    refetchOnWindowFocus: false,
    refetchOnMount: "always",
  });
};

// Hook for reviewer dashboard data with caching
export const useReviewerDashboardData = (userId: string | null) => {
  return useQuery({
    queryKey: queryKeys.reviewerDashboard(userId || ""),
    queryFn: () => fetchReviewerDashboardData(userId!),
    enabled: !!userId,
    staleTime: 2 * 60 * 1000, // 2 minutes (reviewer data changes more frequently)
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: "always",
  });
};

// Hook for individual user profile data
export const useUserProfile = (userId: string | null) => {
  return useQuery({
    queryKey: queryKeys.userProfile(userId || ""),
    queryFn: () => DatabaseService.getCurrentUser(userId!),
    enabled: !!userId,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
};

// Hook for user colleges
export const useUserColleges = (userId: string | null) => {
  return useQuery({
    queryKey: queryKeys.userColleges(userId || ""),
    queryFn: () => DatabaseService.getUserColleges(userId!),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });
};

// Hook for user essays
export const useUserEssays = (userId: string | null) => {
  return useQuery({
    queryKey: queryKeys.userEssays(userId || ""),
    queryFn: () => DatabaseService.getUserEssays(userId!),
    enabled: !!userId,
    staleTime: 2 * 60 * 1000, // 2 minutes (essays change frequently)
  });
};

// Hook for subscription data
export const useSubscriptionData = (userId: string | null) => {
  return useQuery({
    queryKey: queryKeys.subscription(userId || ""),
    queryFn: () => DatabaseService.checkUserCanAnalyze(userId!),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });
};

// Hook for planning data with caching
export const usePlanningData = (userId: string | null) => {
  return useQuery({
    queryKey: queryKeys.planningData(userId || ""),
    queryFn: () => fetchPlanningData(userId!),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false, // Don't refetch on every mount - use cache
  });
};

// Mutation for updating essays
export const useUpdateEssay = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ essayId, updates }: { essayId: string; updates: any }) =>
      DatabaseService.updateEssay(essayId, updates),
    onSuccess: (data, { essayId }) => {
      // Invalidate and refetch essay queries
      queryClient.invalidateQueries({ queryKey: ["essays"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
};

// Mutation for creating essays
export const useCreateEssay = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      title,
      userId,
      collegeId,
      content,
      prompt,
      wordLimit,
    }: {
      title: string;
      userId: string;
      collegeId?: string;
      content?: string;
      prompt?: string;
      wordLimit?: number;
    }) =>
      DatabaseService.createEssay(title, userId, collegeId, content, prompt, wordLimit),
    onSuccess: () => {
      // Invalidate and refetch essay queries
      queryClient.invalidateQueries({ queryKey: ["essays"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
};

// Mutation for deleting essays
export const useDeleteEssay = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (essayId: string) => DatabaseService.deleteEssay(essayId),
    onSuccess: () => {
      // Invalidate and refetch essay queries
      queryClient.invalidateQueries({ queryKey: ["essays"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
};

// Mutation for creating planning colleges
export const useCreatePlanningCollege = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      collegeData,
    }: {
      userId: string;
      collegeData: any;
    }) => DatabaseService.createPlanningCollege(userId, collegeData),
    onSuccess: () => {
      // Invalidate planning data and dashboard to refetch
      queryClient.invalidateQueries({ queryKey: ["planningData"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
};

// Mutation for creating planning milestones
export const useCreatePlanningMilestone = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      milestoneData,
    }: {
      userId: string;
      milestoneData: any;
    }) => DatabaseService.createPlanningMilestone(userId, milestoneData),
    onSuccess: () => {
      // Invalidate planning data to refetch
      queryClient.invalidateQueries({ queryKey: ["planningData"] });
    },
  });
};

// Mutation for updating planning milestones
export const useUpdatePlanningMilestone = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      milestoneId,
      milestoneData,
    }: {
      milestoneId: string;
      milestoneData: any;
    }) => DatabaseService.updatePlanningMilestone(milestoneId, milestoneData),
    onSuccess: () => {
      // Invalidate planning data to refetch
      queryClient.invalidateQueries({ queryKey: ["planningData"] });
    },
  });
};

// Mutation for deleting planning colleges
export const useDeletePlanningCollege = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (collegeId: string) =>
      DatabaseService.deletePlanningCollege(collegeId),
    onSuccess: () => {
      // Invalidate planning data to refetch
      queryClient.invalidateQueries({ queryKey: ["planningData"] });
    },
  });
};

// Mutation for updating planning colleges
export const useUpdatePlanningCollege = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      collegeId,
      collegeData,
    }: {
      collegeId: string;
      collegeData: any;
    }) => DatabaseService.updatePlanningCollege(collegeId, collegeData),
    onSuccess: () => {
      // Invalidate planning data to refetch
      queryClient.invalidateQueries({ queryKey: ["planningData"] });
    },
  });
};

// Mutation for deleting planning milestones
export const useDeletePlanningMilestone = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (milestoneId: string) =>
      DatabaseService.deletePlanningMilestone(milestoneId),
    onSuccess: () => {
      // Invalidate planning data to refetch
      queryClient.invalidateQueries({ queryKey: ["planningData"] });
    },
  });
};

// Hook for planning events (calendar) with caching
export const usePlanningEvents = (userId: string | null) => {
  return useQuery({
    queryKey: ["planningEvents", userId || ""],
    queryFn: () => fetchPlanningEvents(userId!),
    enabled: !!userId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });
};

// Utility function to prefetch dashboard data
export const prefetchDashboardData = (queryClient: any, userId: string) => {
  return queryClient.prefetchQuery({
    queryKey: queryKeys.dashboard(userId),
    queryFn: () => fetchDashboardData(userId),
    staleTime: 5 * 60 * 1000,
  });
};

// Utility function to prefetch reviewer dashboard data
export const prefetchReviewerDashboardData = (
  queryClient: any,
  userId: string
) => {
  return queryClient.prefetchQuery({
    queryKey: queryKeys.reviewerDashboard(userId),
    queryFn: () => fetchReviewerDashboardData(userId),
    staleTime: 2 * 60 * 1000,
  });
};
